import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Search,
  Filter,
  Image as ImageIcon,
  Plus,
  X,
  Check,
  Trash2,
  Eye,
  EyeOff,
  Pencil,
  Copy,
  Upload,
  Loader2,
  Link as LinkIcon,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import {
  getSharedMedia,
  uploadSharedMedia,
  updateSharedMedia,
  deleteSharedMedia,
  deleteMultipleSharedMedia,
  toggleSharedMediaActive,
  getProductsForSelection,
  formatFileSize,
} from '../../services/sharedMediaService';
import {
  SHARED_MEDIA_CATEGORIES,
  SHARED_MEDIA_CATEGORY_LABELS,
  type SharedMedia,
  type SharedMediaCategory,
  type SharedMediaFilters,
  type UpdateSharedMediaInput,
} from '../../types/sharedMedia';
import { logger } from '../../lib/logger';

export function AdminMedia() {
  const { user } = useAuth();
  const [mediaList, setMediaList] = useState<SharedMedia[]>([]);
  const [products, setProducts] = useState<{ id: string; name: string; slug: string }[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<SharedMediaCategory | ''>('');
  const [productFilter, setProductFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Modals
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [editingMedia, setEditingMedia] = useState<SharedMedia | null>(null);
  const [viewingMedia, setViewingMedia] = useState<SharedMedia | null>(null);

  const loadMedia = useCallback(async () => {
    setIsLoading(true);
    try {
      const filters: SharedMediaFilters = {};
      if (search) filters.search = search;
      if (categoryFilter) filters.category = categoryFilter;
      if (productFilter) filters.product_id = productFilter;
      if (statusFilter !== 'all') filters.is_active = statusFilter === 'active';

      const data = await getSharedMedia(filters);
      setMediaList(data);
    } catch (error) {
      logger.error('Error loading shared media:', error);
    } finally {
      setIsLoading(false);
    }
  }, [search, categoryFilter, productFilter, statusFilter]);

  useEffect(() => {
    loadMedia();
  }, [loadMedia]);

  useEffect(() => {
    getProductsForSelection().then(setProducts).catch(logger.error);
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    loadMedia();
  };

  const clearFilters = () => {
    setSearch('');
    setCategoryFilter('');
    setProductFilter('');
    setStatusFilter('all');
  };

  const hasActiveFilters = search || categoryFilter || productFilter || statusFilter !== 'all';

  const toggleSelect = (id: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const selectAll = () => {
    if (selectedIds.size === mediaList.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(mediaList.map((m) => m.id)));
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.size === 0) return;
    if (!confirm(`${selectedIds.size} Datei(en) wirklich löschen?`)) return;

    try {
      await deleteMultipleSharedMedia(Array.from(selectedIds));
      setSelectedIds(new Set());
      await loadMedia();
    } catch (error) {
      logger.error('Delete error:', error);
    }
  };

  const handleToggleActive = async (media: SharedMedia) => {
    try {
      await toggleSharedMediaActive(media.id);
      await loadMedia();
    } catch (error) {
      logger.error('Toggle error:', error);
    }
  };

  const handleUploadComplete = async () => {
    setShowUploadModal(false);
    await loadMedia();
  };

  const handleEditComplete = async () => {
    setEditingMedia(null);
    await loadMedia();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white">Geteilte Medien</h1>
          <p className="text-slate-400 mt-1">{mediaList.length} Medien für alle Nutzer</p>
        </div>
        <button
          onClick={() => setShowUploadModal(true)}
          className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-orange-500 text-white rounded-xl hover:bg-orange-600 transition-colors font-medium"
        >
          <Plus className="w-5 h-5" />
          Hochladen
        </button>
      </div>

      {/* Search and Filters */}
      <div className="bg-slate-900 border border-slate-800 rounded-2xl p-4">
        <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Dateiname suchen..."
              className="w-full pl-12 pr-4 py-2.5 bg-slate-800 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>
          <button
            type="button"
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl transition-colors ${
              hasActiveFilters
                ? 'bg-orange-500/20 text-orange-400 border border-orange-500/50'
                : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
            }`}
          >
            <Filter className="w-4 h-4" />
            Filter
            {hasActiveFilters && <span className="w-2 h-2 bg-orange-500 rounded-full" />}
          </button>
          <button
            type="submit"
            className="px-6 py-2.5 bg-orange-500 text-white rounded-xl hover:bg-orange-600 transition-colors font-medium"
          >
            Suchen
          </button>
        </form>

        {/* Filter Panel */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-slate-800 flex flex-wrap gap-4">
            <div>
              <label className="block text-sm text-slate-400 mb-2">Kategorie</label>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value as SharedMediaCategory | '')}
                className="px-4 py-2 bg-slate-800 border border-slate-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                <option value="">Alle</option>
                {SHARED_MEDIA_CATEGORIES.map((cat) => (
                  <option key={cat} value={cat}>
                    {SHARED_MEDIA_CATEGORY_LABELS[cat]}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-2">Produkt</label>
              <select
                value={productFilter}
                onChange={(e) => setProductFilter(e.target.value)}
                className="px-4 py-2 bg-slate-800 border border-slate-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                <option value="">Alle</option>
                {products.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-2">Status</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as 'all' | 'active' | 'inactive')}
                className="px-4 py-2 bg-slate-800 border border-slate-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                <option value="all">Alle</option>
                <option value="active">Aktiv</option>
                <option value="inactive">Inaktiv</option>
              </select>
            </div>
            {hasActiveFilters && (
              <div className="flex items-end">
                <button
                  onClick={clearFilters}
                  className="flex items-center gap-2 px-4 py-2 text-slate-400 hover:text-white transition-colors"
                >
                  <X className="w-4 h-4" />
                  Filter zurücksetzen
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Selection Actions */}
      {selectedIds.size > 0 && (
        <div className="flex items-center gap-4 bg-slate-900 border border-slate-800 rounded-xl p-3">
          <span className="text-sm text-slate-400">{selectedIds.size} ausgewählt</span>
          <button
            onClick={handleDeleteSelected}
            className="flex items-center gap-2 px-4 py-2 text-red-400 bg-red-500/10 rounded-lg hover:bg-red-500/20 transition-colors"
          >
            <Trash2 className="w-4 h-4" />
            Löschen
          </button>
          <button
            onClick={() => setSelectedIds(new Set())}
            className="p-2 text-slate-400 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Media Grid */}
      <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : mediaList.length === 0 ? (
          <div className="text-center py-20">
            <ImageIcon className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400">Keine Medien gefunden</p>
            <button
              onClick={() => setShowUploadModal(true)}
              className="mt-4 inline-flex items-center gap-2 text-orange-400 hover:text-orange-300"
            >
              <Plus className="w-4 h-4" />
              Erstes Medium hochladen
            </button>
          </div>
        ) : (
          <div className="p-4">
            <div className="flex items-center justify-between mb-4">
              <button
                onClick={selectAll}
                className="text-sm text-slate-400 hover:text-white transition-colors"
              >
                {selectedIds.size === mediaList.length ? 'Auswahl aufheben' : 'Alle auswählen'}
              </button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
              {mediaList.map((media) => (
                <MediaCard
                  key={media.id}
                  media={media}
                  isSelected={selectedIds.has(media.id)}
                  onSelect={() => toggleSelect(media.id)}
                  onView={() => setViewingMedia(media)}
                  onEdit={() => setEditingMedia(media)}
                  onToggleActive={() => handleToggleActive(media)}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Upload Modal */}
      {showUploadModal && user && (
        <UploadModal
          userId={user.id}
          products={products}
          onClose={() => setShowUploadModal(false)}
          onComplete={handleUploadComplete}
        />
      )}

      {/* Edit Modal */}
      {editingMedia && (
        <EditModal
          media={editingMedia}
          products={products}
          onClose={() => setEditingMedia(null)}
          onComplete={handleEditComplete}
        />
      )}

      {/* View Modal */}
      {viewingMedia && (
        <ViewModal
          media={viewingMedia}
          onClose={() => setViewingMedia(null)}
          onEdit={() => {
            setViewingMedia(null);
            setEditingMedia(viewingMedia);
          }}
        />
      )}
    </div>
  );
}

// Media Card Component
function MediaCard({
  media,
  isSelected,
  onSelect,
  onView,
  onEdit,
  onToggleActive,
}: {
  media: SharedMedia;
  isSelected: boolean;
  onSelect: () => void;
  onView: () => void;
  onEdit: () => void;
  onToggleActive: () => void;
}) {
  return (
    <div
      className={`relative group aspect-square rounded-xl overflow-hidden cursor-pointer border-2 transition-colors ${
        isSelected ? 'border-orange-500' : 'border-transparent hover:border-slate-600'
      } ${!media.is_active ? 'opacity-60' : ''}`}
    >
      <img
        src={media.url}
        alt={media.alt_text || media.filename}
        className="w-full h-full object-cover"
        onClick={onView}
      />

      {/* Category Badge */}
      <div className="absolute bottom-2 left-2 px-2 py-1 bg-black/70 rounded text-xs text-white">
        {SHARED_MEDIA_CATEGORY_LABELS[media.category]}
      </div>

      {/* Product Link */}
      {media.product && (
        <div className="absolute bottom-2 right-2">
          <LinkIcon className="w-4 h-4 text-orange-400" />
        </div>
      )}

      {/* Inactive Badge */}
      {!media.is_active && (
        <div className="absolute top-2 right-2 px-2 py-1 bg-red-500/80 rounded text-xs text-white">
          Inaktiv
        </div>
      )}

      {/* Hover Overlay */}
      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onEdit();
          }}
          className="p-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
          title="Bearbeiten"
        >
          <Pencil className="w-4 h-4 text-white" />
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggleActive();
          }}
          className="p-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
          title={media.is_active ? 'Deaktivieren' : 'Aktivieren'}
        >
          {media.is_active ? (
            <EyeOff className="w-4 h-4 text-white" />
          ) : (
            <Eye className="w-4 h-4 text-white" />
          )}
        </button>
      </div>

      {/* Select Checkbox */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onSelect();
        }}
        className={`absolute top-2 left-2 w-6 h-6 rounded-md flex items-center justify-center transition-colors ${
          isSelected
            ? 'bg-orange-500 text-white'
            : 'bg-black/40 text-white opacity-0 group-hover:opacity-100'
        }`}
      >
        <Check className="w-4 h-4" />
      </button>
    </div>
  );
}

// Upload Modal Component
function UploadModal({
  userId,
  products,
  onClose,
  onComplete,
}: {
  userId: string;
  products: { id: string; name: string; slug: string }[];
  onClose: () => void;
  onComplete: () => void;
}) {
  const [files, setFiles] = useState<File[]>([]);
  const [category, setCategory] = useState<SharedMediaCategory>('other');
  const [productId, setProductId] = useState('');
  const [altText, setAltText] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files;
    if (selectedFiles) {
      setFiles(Array.from(selectedFiles).filter((f) => f.type.startsWith('image/')));
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const droppedFiles = e.dataTransfer.files;
    if (droppedFiles) {
      setFiles(Array.from(droppedFiles).filter((f) => f.type.startsWith('image/')));
    }
  };

  const handleUpload = async () => {
    if (files.length === 0) return;

    setUploading(true);
    setUploadProgress(0);

    try {
      for (let i = 0; i < files.length; i++) {
        await uploadSharedMedia(
          userId,
          files[i],
          category,
          productId || undefined,
          altText || undefined
        );
        setUploadProgress(Math.round(((i + 1) / files.length) * 100));
      }
      onComplete();
    } catch (error) {
      logger.error('Upload error:', error);
      alert('Upload fehlgeschlagen');
    } finally {
      setUploading(false);
    }
  };

  const removeFile = (index: number) => {
    setFiles(files.filter((_, i) => i !== index));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm !mt-0">
      <div className="bg-slate-900 border border-slate-800 rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800">
          <h3 className="text-lg font-semibold text-white">Medien hochladen</h3>
          <button
            onClick={onClose}
            className="p-1 hover:bg-slate-800 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Drop Zone */}
          <div
            onDrop={handleDrop}
            onDragOver={(e) => e.preventDefault()}
            onClick={() => fileInputRef.current?.click()}
            className="border-2 border-dashed border-slate-700 rounded-xl p-8 text-center cursor-pointer hover:border-orange-500 transition-colors"
          >
            <Upload className="w-10 h-10 text-slate-500 mx-auto mb-3" />
            <p className="text-slate-400 mb-1">
              Dateien hierher ziehen oder <span className="text-orange-400">durchsuchen</span>
            </p>
            <p className="text-sm text-slate-500">Nur Bilder (max. 10MB)</p>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleFileSelect}
            className="hidden"
          />

          {/* Selected Files */}
          {files.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm text-slate-400">{files.length} Datei(en) ausgewählt</p>
              <div className="max-h-32 overflow-y-auto space-y-1">
                {files.map((file, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between px-3 py-2 bg-slate-800 rounded-lg"
                  >
                    <span className="text-sm text-white truncate">{file.name}</span>
                    <button
                      onClick={() => removeFile(index)}
                      className="p-1 text-slate-400 hover:text-red-400 transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Kategorie *
            </label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value as SharedMediaCategory)}
              className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
            >
              {SHARED_MEDIA_CATEGORIES.map((cat) => (
                <option key={cat} value={cat}>
                  {SHARED_MEDIA_CATEGORY_LABELS[cat]}
                </option>
              ))}
            </select>
          </div>

          {/* Product */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Produkt verknüpfen (optional)
            </label>
            <select
              value={productId}
              onChange={(e) => setProductId(e.target.value)}
              className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
            >
              <option value="">Kein Produkt</option>
              {products.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>

          {/* Alt Text */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Alt-Text (optional)
            </label>
            <input
              type="text"
              value={altText}
              onChange={(e) => setAltText(e.target.value)}
              placeholder="Bildbeschreibung für Barrierefreiheit"
              className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>

          {/* Upload Progress */}
          {uploading && (
            <div>
              <div className="flex items-center justify-between text-sm text-slate-400 mb-2">
                <span>Uploading...</span>
                <span>{uploadProgress}%</span>
              </div>
              <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                <div
                  className="h-full bg-orange-500 transition-all"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-800">
          <button
            onClick={onClose}
            disabled={uploading}
            className="px-4 py-2 text-slate-400 hover:text-white transition-colors disabled:opacity-50"
          >
            Abbrechen
          </button>
          <button
            onClick={handleUpload}
            disabled={files.length === 0 || uploading}
            className="inline-flex items-center gap-2 px-5 py-2 bg-orange-500 text-white rounded-xl hover:bg-orange-600 transition-colors font-medium disabled:opacity-50"
          >
            {uploading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Wird hochgeladen...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4" />
                Hochladen
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// Edit Modal Component
function EditModal({
  media,
  products,
  onClose,
  onComplete,
}: {
  media: SharedMedia;
  products: { id: string; name: string; slug: string }[];
  onClose: () => void;
  onComplete: () => void;
}) {
  const [category, setCategory] = useState<SharedMediaCategory>(media.category);
  const [productId, setProductId] = useState(media.product_id || '');
  const [altText, setAltText] = useState(media.alt_text || '');
  const [isActive, setIsActive] = useState(media.is_active);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      const input: UpdateSharedMediaInput = {
        category,
        product_id: productId || null,
        alt_text: altText,
        is_active: isActive,
      };
      await updateSharedMedia(media.id, input);
      onComplete();
    } catch (error) {
      logger.error('Save error:', error);
      alert('Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm('Dieses Medium wirklich löschen?')) return;

    setDeleting(true);
    try {
      await deleteSharedMedia(media.id);
      onComplete();
    } catch (error) {
      logger.error('Delete error:', error);
      alert('Löschen fehlgeschlagen');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm !mt-0">
      <div className="bg-slate-900 border border-slate-800 rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800">
          <h3 className="text-lg font-semibold text-white">Medium bearbeiten</h3>
          <button
            onClick={onClose}
            className="p-1 hover:bg-slate-800 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Preview */}
          <div className="aspect-video bg-slate-800 rounded-xl overflow-hidden flex items-center justify-center">
            <img
              src={media.url}
              alt={media.alt_text || media.filename}
              className="max-w-full max-h-full object-contain"
            />
          </div>

          {/* Filename */}
          <div>
            <p className="text-sm text-slate-400 mb-1">Dateiname</p>
            <p className="text-white">{media.filename}</p>
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Kategorie</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value as SharedMediaCategory)}
              className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
            >
              {SHARED_MEDIA_CATEGORIES.map((cat) => (
                <option key={cat} value={cat}>
                  {SHARED_MEDIA_CATEGORY_LABELS[cat]}
                </option>
              ))}
            </select>
          </div>

          {/* Product */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Produkt verknüpfen
            </label>
            <select
              value={productId}
              onChange={(e) => setProductId(e.target.value)}
              className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
            >
              <option value="">Kein Produkt</option>
              {products.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>

          {/* Alt Text */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Alt-Text</label>
            <input
              type="text"
              value={altText}
              onChange={(e) => setAltText(e.target.value)}
              placeholder="Bildbeschreibung"
              className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>

          {/* Active Status */}
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-300">Status</p>
              <p className="text-sm text-slate-500">
                {isActive ? 'Für alle Nutzer sichtbar' : 'Nur für Admins sichtbar'}
              </p>
            </div>
            <button
              onClick={() => setIsActive(!isActive)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                isActive ? 'bg-orange-500' : 'bg-slate-700'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  isActive ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>

        <div className="flex items-center justify-between px-6 py-4 border-t border-slate-800">
          <button
            onClick={handleDelete}
            disabled={deleting || saving}
            className="inline-flex items-center gap-2 px-4 py-2 text-red-400 hover:bg-red-500/10 rounded-lg transition-colors disabled:opacity-50"
          >
            {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
            Löschen
          </button>
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              disabled={saving}
              className="px-4 py-2 text-slate-400 hover:text-white transition-colors disabled:opacity-50"
            >
              Abbrechen
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center gap-2 px-5 py-2 bg-orange-500 text-white rounded-xl hover:bg-orange-600 transition-colors font-medium disabled:opacity-50"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Wird gespeichert...
                </>
              ) : (
                'Speichern'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// View Modal Component
function ViewModal({
  media,
  onClose,
  onEdit,
}: {
  media: SharedMedia;
  onClose: () => void;
  onEdit: () => void;
}) {
  const [copied, setCopied] = useState(false);

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(media.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      logger.error('Copy error:', error);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm !mt-0">
      <div className="bg-slate-900 border border-slate-800 rounded-2xl shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800">
          <h3 className="text-lg font-semibold text-white truncate pr-4">{media.filename}</h3>
          <button
            onClick={onClose}
            className="p-1 hover:bg-slate-800 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="aspect-video bg-slate-800 flex items-center justify-center">
            <img
              src={media.url}
              alt={media.alt_text || media.filename}
              className="max-w-full max-h-full object-contain"
            />
          </div>

          <div className="p-6 space-y-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-slate-500 mb-1">Kategorie</p>
                <p className="text-white">{SHARED_MEDIA_CATEGORY_LABELS[media.category]}</p>
              </div>
              <div>
                <p className="text-slate-500 mb-1">Status</p>
                <span
                  className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                    media.is_active
                      ? 'bg-emerald-500/20 text-emerald-400'
                      : 'bg-red-500/20 text-red-400'
                  }`}
                >
                  {media.is_active ? 'Aktiv' : 'Inaktiv'}
                </span>
              </div>
              <div>
                <p className="text-slate-500 mb-1">Dateigröße</p>
                <p className="text-white">{formatFileSize(media.size)}</p>
              </div>
              {media.width && media.height && (
                <div>
                  <p className="text-slate-500 mb-1">Abmessungen</p>
                  <p className="text-white">
                    {media.width} × {media.height} px
                  </p>
                </div>
              )}
              {media.product && (
                <div className="col-span-2">
                  <p className="text-slate-500 mb-1">Verknüpftes Produkt</p>
                  <p className="text-white">{media.product.name}</p>
                </div>
              )}
            </div>

            <div>
              <p className="text-slate-500 mb-2 text-sm">URL</p>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={media.url}
                  readOnly
                  className="flex-1 px-3 py-2 text-sm bg-slate-800 border border-slate-700 rounded-lg text-slate-300"
                />
                <button
                  onClick={copyUrl}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-slate-800 text-slate-300 rounded-lg hover:bg-slate-700 transition-colors text-sm"
                >
                  {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                  {copied ? 'Kopiert' : 'Kopieren'}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-800">
          <button
            onClick={onEdit}
            className="inline-flex items-center gap-2 px-4 py-2 bg-slate-800 text-slate-300 rounded-lg hover:bg-slate-700 transition-colors"
          >
            <Pencil className="w-4 h-4" />
            Bearbeiten
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 text-slate-400 hover:text-white transition-colors"
          >
            Schließen
          </button>
        </div>
      </div>
    </div>
  );
}
